// VENDORED COPY of frontend/src/lib/financial/projections.ts — do not edit here; keep in sync with the main app.
/**
 * Projection calculations for financial items.
 *
 * This module is now fully self-contained. All window.AppLib delegations
 * have been removed -- the TypeScript implementation IS the source of truth.
 *
 * Key functions ported from static/apps/lib/financial-calc.js:
 *  - calculateItemHorizon  (single-item month array)
 *  - calculateHorizon      (multi-item aggregation, legacy return shape)
 *  - getMonthLabels        (short/medium/long label arrays)
 *  - calculateProjection   (simple period-based projection)
 *  - aggregateProjections  (FinancialItem[]-based aggregation)
 */

import type {
  FinancialItem,
  ProjectionOptions,
  HorizonItem,
  HorizonResult,
  MonthLabelFormat,
} from './types';
import { evaluateFormula } from './formulas';
import { getIntervalConfig } from './constants';
import { parseDate, getMonthDiff, getDefaultStartDate } from './date-utils';

// ── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Normalise a type string to either `'one_time'` or `'recurring'`.
 * Accepts the varied type strings that come from different API shapes.
 *
 * @param type - Raw type string from the item
 * @param isRecurring - Fallback boolean flag
 * @returns Normalised type
 */
function normalizeType(type: string | undefined, isRecurring: boolean | undefined): 'one_time' | 'recurring' {
  if (type === 'one_time' || type === 'onetime' || type === 'one-time') {
    return 'one_time';
  }
  if (type === 'recurring' || type === 'variable' || type === 'fixed') {
    return 'recurring';
  }
  // Fallback to the boolean recurring flag
  return isRecurring ? 'recurring' : 'one_time';
}

// ── Primary API ──────────────────────────────────────────────────────────────

/**
 * Options accepted by calculateItemHorizon and calculateHorizon.
 * Superset of ProjectionOptions to accept Date objects as well as strings.
 */
export interface HorizonOptions {
  horizonMonths?: number;
  startDate?: Date | string;
  debug?: boolean;
}

/**
 * Calculate a monthly horizon array for a single item.
 *
 * This is a direct port of window.AppLib.calculateItemHorizon from
 * financial-calc.js. It handles one-time items, recurring items with
 * interval/formula/cap/termination, and effective_date offsets.
 *
 * @param item - Cost or savings item (loose shape to accept raw API payloads)
 * @param options - Horizon calculation options
 * @returns Array of monthly values with length === horizonMonths
 */
export function calculateItemHorizon(
  item: HorizonItem | FinancialItem,
  options: HorizonOptions = {},
): number[] {
  const {
    horizonMonths = 36,
    startDate: startDateInput,
    debug = false,
  } = options;

  const startDate =
    startDateInput instanceof Date
      ? startDateInput
      : startDateInput
        ? new Date(startDateInput)
        : getDefaultStartDate();

  // Initialise with zeros
  const values: number[] = new Array(horizonMonths).fill(0);

  // Normalise item properties (accept both FinancialItem and HorizonItem shapes)
  const type = normalizeType(
    (item as HorizonItem).type,
    (item as FinancialItem).recurring,
  );
  const baseValue = (item as HorizonItem).value ?? (item as HorizonItem).cost ?? 0;
  const effectiveDate = (item as HorizonItem).effective_date
    ? parseDate((item as HorizonItem).effective_date as string | Date)
    : startDate;
  const intervalConfig = getIntervalConfig(
    (item as HorizonItem).interval ?? (item as FinancialItem).recurring_interval ?? 'monthly',
  );
  const formula = item.formula || 'base';
  const cap = item.formula_cap ?? null;
  const terminatesAfter = item.recurring_terminates || 0;

  // Month index where item becomes effective relative to horizon start
  const effectiveMonthIndex = getMonthDiff(startDate, effectiveDate);

  if (debug) {
    console.group('Horizon Calculation');
    console.log('Input item:', item);
    console.log('Normalised:', {
      type,
      baseValue,
      effectiveDate: effectiveDate.toISOString().slice(0, 10),
      interval: intervalConfig.label,
      intervalMonths: intervalConfig.months,
      formula,
      cap,
      terminatesAfter,
    });
    console.log('Horizon:', {
      startDate: startDate.toISOString().slice(0, 10),
      horizonMonths,
      effectiveMonthIndex,
    });
  }

  // If effective date is beyond the horizon, return zeros
  if (effectiveMonthIndex >= horizonMonths) {
    if (debug) {
      console.log('Effective date is beyond horizon - returning zeros');
      console.groupEnd();
    }
    return values;
  }

  const startIndex = Math.max(0, effectiveMonthIndex);

  if (type === 'one_time') {
    // One-time: single value in the effective month only.
    // When a formula is set (and not null / "base"), evaluate it at period=0
    // so one-time rows can carry growth-like adjustments at booking time.
    if (startIndex < horizonMonths) {
      let oneTimeValue = baseValue;
      if (formula != null && formula !== 'base' && formula !== '') {
        oneTimeValue = evaluateFormula(formula, baseValue, 0, baseValue, cap);
      }
      values[startIndex] = oneTimeValue;
      if (debug) {
        console.log(
          `One-time: base=${baseValue}, evaluated=${oneTimeValue}, ` +
            `formula=${formula ?? 'null'} at month index ${startIndex}`,
        );
      }
    }
  } else {
    // Recurring: evaluate formula at each interval
    const intervalMonths = intervalConfig.months;
    let prev = baseValue;
    let periodCount = 0;

    for (let monthIndex = startIndex; monthIndex < horizonMonths; monthIndex++) {
      const monthsFromEffective = monthIndex - startIndex;

      // Check if this month is an interval hit
      if (monthsFromEffective % intervalMonths === 0) {
        // Check termination
        if (terminatesAfter > 0 && periodCount >= terminatesAfter) {
          if (debug) {
            console.log(`Terminated after ${periodCount} periods`);
          }
          break;
        }

        // Calculate value for this period
        const periodValue = evaluateFormula(formula, baseValue, periodCount, prev, cap);
        values[monthIndex] = periodValue;

        prev = periodValue;
        periodCount++;
      }
    }

    if (debug) {
      console.log(`Recurring: ${periodCount} periods calculated`);
    }
  }

  if (debug) {
    const total = values.reduce((sum, v) => sum + v, 0);
    const nonZeroMonths = values.filter(v => v > 0).length;
    console.log('Result:', {
      total: total.toFixed(2),
      nonZeroMonths,
      firstValue: values.find(v => v > 0) || 0,
      lastValue: values.filter(v => v > 0).pop() || 0,
    });
    console.groupEnd();
  }

  return values;
}

/**
 * Calculate horizon for multiple items and aggregate.
 *
 * This matches the legacy window.AppLib.calculateHorizon return shape:
 * `{ monthly, total, items }`.
 *
 * @param items - Array of cost/savings items
 * @param options - Calculation options
 * @returns Aggregated horizon result
 */
export function calculateHorizon(
  items: Array<HorizonItem | FinancialItem>,
  options: HorizonOptions = {},
): HorizonResult {
  const {
    horizonMonths = 36,
    startDate: startDateInput,
  } = options;

  const startDate =
    startDateInput instanceof Date
      ? startDateInput
      : startDateInput
        ? new Date(startDateInput)
        : getDefaultStartDate();

  // Calculate each item's horizon
  const itemResults = items.map(item => ({
    item: item as HorizonItem,
    values: calculateItemHorizon(item, { horizonMonths, startDate }),
  }));

  // Aggregate monthly values
  const monthly: number[] = new Array(horizonMonths).fill(0);
  itemResults.forEach(({ values }) => {
    values.forEach((value, index) => {
      monthly[index] += value;
    });
  });

  // Calculate total
  const total = monthly.reduce((sum, val) => sum + val, 0);

  return {
    monthly,
    total,
    items: itemResults,
  };
}

/**
 * Get month labels for a horizon chart.
 *
 * @param options - Label generation options
 * @param options.horizonMonths - Number of months (default 36)
 * @param options.startDate - Start date (default: first of current month)
 * @param options.format - Label format: 'short' | 'medium' | 'long'
 * @returns Array of formatted month labels
 */
export function getMonthLabels(options?: {
  horizonMonths?: number;
  startDate?: Date | string;
  format?: MonthLabelFormat;
}): string[];

/**
 * Legacy overload: getMonthLabels(count, startDate?)
 * Maintained for backward compatibility with existing TypeScript consumers.
 */
export function getMonthLabels(count: number, startDate?: string): string[];

/** Implementation */
export function getMonthLabels(
  countOrOptions?: number | {
    horizonMonths?: number;
    startDate?: Date | string;
    format?: MonthLabelFormat;
  },
  legacyStartDate?: string,
): string[] {
  let horizonMonths: number;
  let startDate: Date;
  let format: MonthLabelFormat;

  if (typeof countOrOptions === 'number') {
    // Legacy call signature: getMonthLabels(count, startDate?)
    horizonMonths = countOrOptions;
    startDate = legacyStartDate ? new Date(legacyStartDate) : getDefaultStartDate();
    format = 'medium';
  } else {
    // New options-object signature
    const opts = countOrOptions ?? {};
    horizonMonths = opts.horizonMonths ?? 36;
    const sd = opts.startDate;
    startDate = sd instanceof Date ? sd : sd ? new Date(sd) : getDefaultStartDate();
    format = opts.format ?? 'short';
  }

  const labels: string[] = [];
  const date = new Date(startDate);

  for (let i = 0; i < horizonMonths; i++) {
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);

    switch (format) {
      case 'medium':
        labels.push(`${month} ${year}`);
        break;
      case 'long':
        labels.push(date.toLocaleString('default', { month: 'long', year: 'numeric' }));
        break;
      default: // short
        labels.push(month);
    }

    date.setMonth(date.getMonth() + 1);
  }

  return labels;
}

// ── Convenience wrappers (existing API, kept for backward compat) ────────────

/**
 * Calculate projected values for multiple periods (simple projection).
 *
 * @param base - Base value
 * @param formula - Formula string
 * @param cap - Optional maximum cap
 * @param periods - Number of periods to calculate
 * @param _interval - Interval (unused, kept for API compat)
 * @returns Array of calculated values
 */
export function calculateProjection(
  base: number,
  formula: string,
  cap: number | null = null,
  periods: number = 12,
  _interval: number = 1,
): number[] {
  const values: number[] = [];
  let prev = base;

  for (let i = 0; i < periods; i++) {
    const value = evaluateFormula(formula, base, i, prev, cap);
    values.push(value);
    prev = value;
  }

  return values;
}

/**
 * Aggregate projections from multiple FinancialItem instances.
 *
 * @param items - Array of financial items
 * @param options - Projection options
 * @returns Array of aggregated monthly values
 */
export function aggregateProjections(
  items: FinancialItem[],
  options: ProjectionOptions = {},
): number[] {
  const horizonMonths = options.horizonMonths || 36;
  const totals: number[] = new Array(horizonMonths).fill(0);

  items.forEach((item) => {
    const itemProjection = calculateItemHorizon(item, options);
    itemProjection.forEach((value, index) => {
      if (index < horizonMonths) {
        totals[index] += value;
      }
    });
  });

  return totals;
}

/**
 * Calculate total value across all periods.
 *
 * @param projection - Array of projected values
 * @returns Sum of all values
 */
export function calculateTotalValue(projection: number[]): number {
  return projection.reduce((sum, value) => sum + value, 0);
}
